import * as React from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { getNestedFormErrors } from 'src/shared/functions/getNestedFormErrors';
import { useTranslation } from 'react-i18next';

type ControlledAutocompleteDropdownProps<T> = {
  options: Array<T>;
  nameProp: string;
  label: string;
  name: string;
  callback: (selected: T | null) => void;
  componentWidth?: string;
  placeholder?: string;
  disable?: boolean;
};

export const ControlledAutocompleteDropdown = <T,>(props: ControlledAutocompleteDropdownProps<T>) => {
  const { options, nameProp, label, name, callback, componentWidth = '15rem', placeholder = undefined, disable } = props;
  const { t } = useTranslation();
  const [selectedValue, setSelectedValue] = React.useState<T | null>(null);
  const [inputValue, setInputValue] = React.useState('');

  const { errors } = useFormState();
  const { setValue, getValues } = useFormContext();

  const componentId = `autocomplete-${Date.now().toString(36) + Math.random().toString(36).substring(2)}`;

  let isError = false;
  const errorMessage = getNestedFormErrors(errors, name);
  if (errorMessage) {
    isError = true;
  }

  React.useEffect(() => {
    setSelectedValue(getValues(name));
  }, [getValues, name]);

  return (
    <Autocomplete
      id={componentId}
      options={options}
      autoHighlight
      disabled={disable}
      getOptionLabel={(option) => option[nameProp] || ''}
      isOptionEqualToValue={(option, val) => {
        return val[nameProp] === t('common:form.all') || (placeholder && val[nameProp] === placeholder) || option[nameProp] === val[nameProp];
      }}
      onChange={(_event: any, newValue: T | null) => {
        setValue(name, newValue);
        setSelectedValue(newValue);
        callback(newValue);
      }}
      value={selectedValue || null}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          size='small'
          sx={{ width: componentWidth }}
          label={label}
          error={isError}
          placeholder={placeholder}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            ...params.inputProps,
            autoComplete: 'new-password', // disable autocomplete and autofill
          }}
          helperText={errorMessage ? <em>{errorMessage}</em> : null}
        />
      )}
    />
  );
};
